import { useState } from "react";
import { adminFetch } from "../../lib/adminApi";
import { toast } from "sonner";

export interface CauseItem {
  id: string;
  title_en: string;
  title_tr: string;
  desc_en: string;
  desc_tr: string;
  icon: string;
  color: string;
  sort_order: number;
}

export function useCausesDraft(
  editingCause: Partial<CauseItem> | null,
  setEditingCause: React.Dispatch<React.SetStateAction<Partial<CauseItem> | null>>,
  causes: CauseItem[],
  activeDraft: any,
  setActiveDraft: React.Dispatch<React.SetStateAction<any>>
) {
  const handleSaveDraft = async () => {
    if (!editingCause) return;
    try {
      await adminFetch("/api/admin/drafts", {
        operation: "save",
        item_type: "cause",
        item_id: editingCause.id,
        draft_data: editingCause,
      });
      setActiveDraft(editingCause);
      toast.success("Cause draft saved to staging successfully!");
    } catch (err: any) {
      toast.error(err.message || "Failed to save draft");
    }
  };

  const handleDiscardDraft = async () => {
    if (!editingCause) return;
    if (!confirm("Are you sure you want to discard this staged draft?")) return;
    try {
      await adminFetch("/api/admin/drafts", {
        operation: "delete",
        item_type: "cause",
        item_id: editingCause.id,
      });
      setActiveDraft(null);
      const live = causes.find((c) => c.id === editingCause.id);
      setEditingCause(live || null);
      toast.success("Staged draft discarded successfully!");
    } catch (err: any) {
      toast.error(err.message || "Failed to discard draft");
    }
  };

  return { handleSaveDraft, handleDiscardDraft };
}

async function swapCauseOrder(causeA: CauseItem, causeB: CauseItem, onRefresh: () => void) {
  try {
    await adminFetch("/api/admin/causes", { ...causeA, sort_order: causeB.sort_order });
    await adminFetch("/api/admin/causes", { ...causeB, sort_order: causeA.sort_order });
    onRefresh();
  } catch (err: any) {
    toast.error(err.message || "Failed to reorder causes");
  }
}

const createDefaultCause = (causesLength: number): Partial<CauseItem> => ({
  id: `cause-${Date.now()}`,
  title_en: "",
  title_tr: "",
  desc_en: "",
  desc_tr: "",
  icon: "ShieldCheck",
  color: "text-brand-primary bg-brand-primary/10 dark:bg-brand-primary/20",
  sort_order: causesLength,
});

export function useCausesManager(causes: CauseItem[], onRefresh: () => void) {
  const [editingCause, setEditingCause] = useState<Partial<CauseItem> | null>(null);
  const [activeDraft, setActiveDraft] = useState<any>(null);

  const { handleSaveDraft, handleDiscardDraft } = useCausesDraft(
    editingCause,
    setEditingCause,
    causes,
    activeDraft,
    setActiveDraft
  );

  const startAdd = () => {
    setActiveDraft(null);
    setEditingCause(createDefaultCause(causes.length));
  };

  const handleEdit = (cause: CauseItem) => {
    setEditingCause(cause);
    adminFetch(`/api/admin/drafts?item_type=cause&item_id=${cause.id}`)
      .then((res) => {
        if (res?.success && res.draft) {
          setActiveDraft(res.draft);
          setEditingCause(res.draft);
          toast.info("Unpublished staged draft loaded for editing.");
        } else {
          setActiveDraft(null);
        }
      })
      .catch(() => setActiveDraft(null));
  };

  const handleSave = async () => {
    if (!editingCause?.title_en || !editingCause?.desc_en) {
      toast.error("Title and Description (English) are required!");
      return;
    }
    try {
      await adminFetch("/api/admin/drafts", {
        operation: "promote",
        item_type: "cause",
        item_id: editingCause.id,
        draft_data: editingCause,
      });
      toast.success("Community value/cause saved successfully!");
      setEditingCause(null);
      setActiveDraft(null);
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to save cause");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this cause?")) return;
    try {
      await adminFetch("/api/admin/causes", { operation: "delete", id });
      await adminFetch("/api/admin/drafts", {
        operation: "delete",
        item_type: "cause",
        item_id: id,
      });
      toast.success("Cause deleted successfully!");
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to delete cause");
    }
  };

  const handleMove = async (index: number, direction: "up" | "down") => {
    const sortedCauses = [...causes].sort((a, b) => a.sort_order - b.sort_order);
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= sortedCauses.length) return;

    const causeA = sortedCauses[index];
    const causeB = sortedCauses[swapIndex];
    if (causeA && causeB) {
      await swapCauseOrder(causeA, causeB, onRefresh);
    }
  };

  return {
    editingCause,
    setEditingCause,
    activeDraft,
    setActiveDraft,
    startAdd,
    handleEdit,
    handleSaveDraft,
    handleDiscardDraft,
    handleSave,
    handleDelete,
    handleMove,
  };
}

export default useCausesManager;
